// client/src/components/layout/Sidebar.jsx
import { forwardRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import NavItem from './NavItem';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import logo from '../../styles/logo1.png';

/* ---------------- Icons (defined first so they're in scope) ---------------- */

function DashboardIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M3 3h8v8H3V3Z" />
      <path d="M13 3h8v5h-8V3Z" />
      <path d="M13 10h8v11h-8V10Z" />
      <path d="M3 13h8v8H3v-8Z" />
    </svg>
  );
}

function FolderIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" />
    </svg>
  );
}

function CalculatorIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 7h8" />
      <path d="M8 11h8" />
      <path d="M8 15h2" />
      <path d="M12 15h2" />
      <path d="M16 15h0.01" />
      <path d="M8 19h2" />
      <path d="M12 19h2" />
      <path d="M16 19h0.01" />
    </svg>
  );
}

function SparklesIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 2v4" />
      <path d="M12 18v4" />
      <path d="M4.93 4.93l2.83 2.83" />
      <path d="M16.24 16.24l2.83 2.83" />
      <path d="M2 12h4" />
      <path d="M18 12h4" />
      <path d="M7 12a5 5 0 1 1 10 0 5 5 0 0 1-10 0Z" />
    </svg>
  );
}

function UserIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
      <path d="M4 20a8 8 0 0 1 16 0" />
    </svg>
  );
}

function ShieldIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 3 4 6v6c0 5 3.6 9.4 8 10 4.4-.6 8-5 8-10V6l-8-3Z" />
      <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronLeftIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15 18 9 12l6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRightIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CommunityIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
      <path d="M6 22a6 6 0 0 1 12 0" />
      <circle cx="5" cy="8" r="3" />
      <circle cx="19" cy="8" r="3" />
      <path d="M2 20a4 4 0 0 1 5.5-3.7" />
      <path d="M16.5 16.3A4 4 0 0 1 22 20" />
    </svg>
  );
}

function CloseIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 6 6 18" strokeLinecap="round" />
      <path d="m6 6 12 12" strokeLinecap="round" />
    </svg>
  );
}

/* ---------------- helpers ---------------- */

function getInitials(email) {
  if (!email) return '?';
  return email
    .split('@')[0]
    .split(/[._-]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '?';
}

/* ---------------- component ---------------- */

const Sidebar = forwardRef(function Sidebar(
  { open, setOpen, mobile = false, ...rest },
  ref
) {
  const { user, signOut } = useAuth();
  const { permissions } = useOrg();

  const collapsed = !mobile && !open;

  useEffect(() => {
    if (!mobile || !open) return;
    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [mobile, open, setOpen]);

  const handleToggle = () => {
    if (mobile) {
      setOpen(false);
    } else {
      setOpen((prev) => !prev);
    }
  };

  const handleNavigate = mobile ? () => setOpen(false) : undefined;
  const handleSignOut = () => {
    if (mobile) {
      setOpen(false);
    }
    signOut();
  };

  const classes = [
    'glass-panel flex flex-col flex-shrink-0 overflow-y-auto overflow-x-hidden border-r border-white/5 supports-[backdrop-filter]:backdrop-blur-2xl',
    'motion-safe:transition-all motion-safe:duration-300 backdrop-saturate-150',
    'bg-[radial-gradient(circle_at_12%_20%,rgba(94,234,212,0.12),transparent_36%),radial-gradient(circle_at_85%_10%,rgba(246,196,82,0.16),transparent_32%),rgba(6,9,15,0.9)]',
    'shadow-[0_25px_70px_rgba(0,0,0,0.55)]'
  ];

  if (mobile) {
    classes.push(
      'fixed inset-y-0 left-0 z-50 w-72 max-w-full transform shadow-2xl',
      open ? 'translate-x-0' : '-translate-x-full'
    );
  } else {
    classes.push(
      // Desktop: keep sidebar always visible; header has left padding so they won't overlap
      'fixed inset-y-0 left-0 z-40',
      open ? 'w-72' : 'w-[72px]',
      'min-w-[72px]'
    );
  }

  const ControlIcon = mobile ? CloseIcon : collapsed ? ChevronRightIcon : ChevronLeftIcon;
  const email = user?.email ?? '';
  const initials = useMemo(() => getInitials(email), [email]);
  const profileRoute = user ? '/profile' : '/auth';

  const navItems = useMemo(() => {
    const flags = permissions?.featureFlags || {};
    const isEnabled = (value) => value !== false;

    const blueprint = [
      {
        label: 'Dashboard',
        to: '/',
        icon: DashboardIcon,
        exact: true,
        enabled: isEnabled(flags.dashboard),
      },
      {
        label: 'Projects',
        to: '/projects',
        icon: FolderIcon,
        enabled: isEnabled(flags.projects),
      },
      {
        label: 'Estimates',
        to: '/estimate',
        icon: CalculatorIcon,
        enabled: isEnabled(flags.estimates),
      },
      {
        label: 'Vendors',
        to: '/vendors',
        icon: ShieldIcon,
        enabled: true,
      },
      {
        label: 'Design Assistant',
        to: '/design-assistant',
        icon: SparklesIcon,
        exact: true,
        enabled: isEnabled(flags.design),
      },
      {
        label: 'Community Builds',
        to: '/community-builds',
        icon: CommunityIcon,
        enabled: true,
      },
      { label: 'Profile', to: profileRoute, icon: UserIcon, enabled: true },
    ];

    const items = blueprint
      .filter((entry) => entry.enabled)
      .map(({ enabled, ...entry }) => entry);

    if (permissions.canAccessAdmin || permissions.role === 'admin') {
      items.push({ label: 'Admin', to: '/admin', icon: ShieldIcon });
    }
    return items;
  }, [
    profileRoute,
    permissions.featureFlags?.dashboard,
    permissions.featureFlags?.projects,
    permissions.featureFlags?.estimates,
    permissions.featureFlags?.design,
    permissions.canAccessAdmin,
    permissions.role,
  ]);

  const role = mobile ? 'dialog' : 'navigation';
  const ariaModal = mobile ? true : undefined;

  const stopPropagation = (event) => {
    if (mobile) {
      event.stopPropagation();
    }
  };

  return (
    <aside
      {...rest}
      ref={ref}
      role={role}
      aria-label="Main navigation"
      aria-modal={ariaModal}
      className={classes.join(' ')}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
    >
      <div className="relative flex items-center justify-between px-4 py-4">
        <div className="absolute inset-x-4 bottom-0 h-px bg-transparent" />
        {!collapsed ? (
          <>
            <div className="flex items-center gap-3">
              <div className="relative h-10 w-10 overflow-hidden rounded-xl">
                <img src={logo} alt="ArchIntelligence" className="h-full w-full object-contain" />
              </div>
              <span className="text-sm font-semibold tracking-wide text-zinc-100">
                ArchIntelligence
              </span>
            </div>
            <button
              type="button"
              onClick={handleToggle}
              className="rounded-lg p-2 text-zinc-300 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60"
              aria-label={mobile ? 'Close menu' : 'Collapse sidebar'}
              aria-expanded={!mobile ? open : undefined}
            >
              <ControlIcon className="h-5 w-5" />
            </button>
          </>
        ) : (
          <div className="flex w-full flex-col items-center gap-3">
            <div className="relative h-10 w-10 overflow-hidden rounded-xl">
              <img src={logo} alt="ArchIntelligence" className="h-full w-full object-contain" />
            </div>
            <button
              type="button"
              onClick={handleToggle}
              className="rounded-lg p-2 text-zinc-300 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60"
              aria-label="Expand sidebar"
              aria-expanded={false}
            >
              <ControlIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      <nav className="px-3 pb-6" aria-label="Primary">
        <div className="space-y-1">
          {navItems.map((item) => (
            <NavItem
              key={item.to}
              to={item.to}
              exact={item.exact}
              label={item.label}
              icon={item.icon}
              collapsed={collapsed}
              onNavigate={handleNavigate}
            />
          ))}
        </div>
      </nav>

      <div className="mt-auto px-4 pb-5 pt-4">
        {!collapsed ? (
          <div className="glass-panel flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-3 shadow-[0_16px_50px_rgba(0,0,0,0.4)]">
            <div className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-gradient-to-br from-amber-400/20 via-teal-300/10 to-transparent text-sm font-semibold text-amber-100 shadow-[0_12px_30px_-12px_rgba(246,196,82,0.8)]">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-zinc-100">{email || 'Guest'}</div>
              <div className="mt-2 flex gap-3 text-xs">
                <Link
                  to={profileRoute}
                  onClick={handleNavigate}
                  className="font-semibold text-amber-300 transition-colors hover:text-amber-200"
                >
                  Profile
                </Link>
                {user && (
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="text-zinc-300 transition-colors hover:text-white"
                  >
                    Sign out
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <Link
            to={profileRoute}
            onClick={handleNavigate}
            className="flex justify-center"
          >
            <div className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-gradient-to-br from-amber-400/20 via-teal-300/10 to-transparent text-sm font-semibold text-amber-100 shadow-[0_12px_30px_-12px_rgba(246,196,82,0.8)] transition-colors hover:border-amber-400/30">
              {initials}
            </div>
          </Link>
        )}
      </div>
    </aside>
  );
});

export default Sidebar;
